import type { ConfiguracaoVeiculo, Decubito, SentidoCabeca } from './configuracaoVeiculo'
import type { GrauEncarceramento } from './encarceramento'
import type { EstadoClinico, Ocupante } from './ocupantes'

export type ModoExperienciaExtracao = 'demonstracao' | 'cenario'
export type EtapaDecisaoExtracao = 'porque' | 'quando' | 'como' | 'debriefing'
export const ETAPAS_DECISAO_EXTRACAO: readonly EtapaDecisaoExtracao[] = [
  'porque',
  'quando',
  'como',
  'debriefing',
]

export type EtapaXabcde = 'X' | 'A' | 'B' | 'C' | 'D' | 'E'
export const ETAPAS_XABCDE: readonly EtapaXabcde[] = ['X', 'A', 'B', 'C', 'D', 'E']

export interface AchadoXabcde {
  resumo: string
  detalhe: string
  /** Região usada pelo 3D para um destaque futuro; nunca é diagnóstico. */
  regiao?: 'cabeca' | 'torax' | 'abdome' | 'membroInferior' | 'corpoTodo'
  /**
   * `false` = o professor ainda não preencheu este achado ao autorar um
   * cenário. Ausente (cenários do catálogo fixo) equivale a `true`: nenhum
   * dos seis já preenchidos aqui está incompleto. Existe pra "dado ausente"
   * nunca virar "achado normal" por omissão — Fase 5B.
   */
  informado?: boolean
}

export interface AprisionamentoCenario {
  tipo: 'painel' | 'pedais' | 'colunaB' | 'bancoCinto' | 'deformacao'
  descricao: string
}

export interface VitimaCenarioExtracao {
  id: string
  veiculoId: string
  nome: string
  idAncora: string
  idPerfil: string
  estadoClinico: EstadoClinico
  perceptivel: readonly string[]
  urgencia: 'maior' | 'estavel' | 'avaliar'
  encarceramento: GrauEncarceramento
  leituraEncarceramento: string
  aprisionamentos: readonly AprisionamentoCenario[]
  xabcde: Readonly<Record<EtapaXabcde, AchadoXabcde>>
}

export interface VeiculoCenarioExtracao {
  id: string
  nome: string
  configuracao: ConfiguracaoVeiculo
  decubito?: Decubito
  sentidoCabeca?: SentidoCabeca
  estabilidadeInicial: boolean
  riscosIniciais: readonly string[]
  acessoInicial: 'livre' | 'limitado' | 'bloqueado'
}

export interface CenarioExtracao {
  id: string
  nome: string
  resumo: string
  objetivo: string
  veiculos: readonly VeiculoCenarioExtracao[]
  vitimas: readonly VitimaCenarioExtracao[]
  pausas: readonly string[]
}

const xabcde = (parcial: Partial<Record<EtapaXabcde, AchadoXabcde>>): Readonly<Record<EtapaXabcde, AchadoXabcde>> => ({
  X: { resumo: 'Sem hemorragia externa importante percebida', detalhe: 'A inspeção rápida não mostra sangramento externo de grande volume.', regiao: 'corpoTodo' },
  A: { resumo: 'Via aérea aparentemente pérvia', detalhe: 'A vítima responde e mantém a via aérea sem sinal evidente de obstrução.', regiao: 'cabeca' },
  B: { resumo: 'Respiração aparentemente adequada', detalhe: 'Movimento torácico presente, sem alteração evidente nesta avaliação simplificada.', regiao: 'torax' },
  C: { resumo: 'Circulação sem alteração evidente', detalhe: 'Pulso presente e perfusão sem sinal simplificado de deterioração.', regiao: 'corpoTodo' },
  D: { resumo: 'Consciente e orientada', detalhe: 'A vítima responde de forma coerente.', regiao: 'cabeca' },
  E: { resumo: 'Sem outro achado evidente', detalhe: 'A exposição dirigida não acrescenta outro fator relevante à estratégia de retirada.', regiao: 'corpoTodo' },
  ...parcial,
})

const vitima = (
  dados: Omit<VitimaCenarioExtracao, 'idPerfil' | 'xabcde' | 'aprisionamentos'> & {
    idPerfil?: string
    xabcde?: Partial<Record<EtapaXabcde, AchadoXabcde>>
    aprisionamentos?: readonly AprisionamentoCenario[]
  },
): VitimaCenarioExtracao => ({
  idPerfil: dados.idPerfil ?? 'adultoMasculino',
  aprisionamentos: dados.aprisionamentos ?? [],
  ...dados,
  xabcde: xabcde(dados.xabcde ?? {}),
})

export const CENARIOS_EXTRACAO: readonly CenarioExtracao[] = [
  {
    id: 'motorista-estavel',
    nome: '1 · Motorista estável',
    resumo: 'Veículo sobre rodas, vítima consciente e porta do motorista disponível.',
    objetivo: 'Sequência normal de preparação e escolha argumentada da rota.',
    veiculos: [{ id: 'veiculo-a', nome: 'Veículo A', configuracao: 'sobreRodas', estabilidadeInicial: true, riscosIniciais: ['SRS não avaliado'], acessoInicial: 'livre' }],
    vitimas: [vitima({ id: 'vitima-a', veiculoId: 'veiculo-a', nome: 'Vítima A · motorista', idAncora: 'motorista', estadoClinico: 'estavel', perceptivel: ['Consciente', 'Responde', 'Sem hemorragia visível importante'], urgencia: 'estavel', encarceramento: 'fisicoI', leituraEncarceramento: 'As condições clínicas podem impedir a saída autônoma; confirme na avaliação.' })],
    pausas: ['O cenário está estável. O que ainda precisa ser preparado antes de movimentar a vítima?'],
  },
  {
    id: 'motorista-prioridade',
    nome: '2 · Motorista com maior prioridade',
    resumo: 'Alteração clínica, membro inferior preso e porta do motorista comprometida.',
    objetivo: 'Discutir urgência clínica versus tempo e necessidade de criação de espaço.',
    veiculos: [{ id: 'veiculo-a', nome: 'Veículo A', configuracao: 'sobreRodas', estabilidadeInicial: true, riscosIniciais: ['SRS presente', 'porta deformada'], acessoInicial: 'limitado' }],
    vitimas: [vitima({
      id: 'vitima-a', veiculoId: 'veiculo-a', nome: 'Vítima A · motorista', idAncora: 'motorista', estadoClinico: 'grave', perceptivel: ['Confusa', 'Respiração presente', 'Membro inferior preso'], urgencia: 'maior', encarceramento: 'fisicoII', leituraEncarceramento: 'Parte do painel prende diretamente o membro inferior.',
      aprisionamentos: [{ tipo: 'painel', descricao: 'Perna esquerda presa sob o painel' }, { tipo: 'pedais', descricao: 'Pé esquerdo limitado na região dos pedais' }],
      xabcde: {
        B: { resumo: 'Alteração respiratória', detalhe: 'A respiração é presente, porém irregular nesta avaliação simplificada.', regiao: 'torax' },
        C: { resumo: 'Sinais de alteração circulatória', detalhe: 'Perfusão periférica reduzida, achado que aumenta a prioridade clínica.', regiao: 'corpoTodo' },
        D: { resumo: 'Confusa', detalhe: 'Responde, mas apresenta alteração do estado mental.', regiao: 'cabeca' },
        E: { resumo: 'Membro inferior preso', detalhe: 'Painel e pedaleira impedem movimentação direta pela porta.', regiao: 'membroInferior' },
      },
    })],
    pausas: ['A urgência aumentou, mas o membro continua preso. Retirar agora ou criar espaço primeiro?'],
  },
  {
    id: 'duas-mesmo-veiculo',
    nome: '3 · Duas vítimas no mesmo veículo',
    resumo: 'Motorista prioritário; passageira traseira estável ocupa parte da rota.',
    objetivo: 'Mostrar que prioridade clínica e ordem física de retirada podem divergir.',
    veiculos: [{ id: 'veiculo-a', nome: 'Veículo A', configuracao: 'sobreRodas', estabilidadeInicial: true, riscosIniciais: ['SRS não protegido'], acessoInicial: 'limitado' }],
    vitimas: [
      vitima({ id: 'vitima-a', veiculoId: 'veiculo-a', nome: 'Vítima A · motorista', idAncora: 'motorista', estadoClinico: 'grave', perceptivel: ['Confusa', 'Respiração presente', 'Presa no veículo'], urgencia: 'maior', encarceramento: 'fisicoII', leituraEncarceramento: 'A deformação prende o membro inferior.', aprisionamentos: [{ tipo: 'painel', descricao: 'Perna presa sob o painel' }], xabcde: { D: { resumo: 'Confusa', detalhe: 'Alteração do estado mental aumenta a prioridade clínica.', regiao: 'cabeca' }, E: { resumo: 'Perna presa no painel', detalhe: 'A vítima não pode ser movida sem liberação/criação de espaço.', regiao: 'membroInferior' } } }),
      vitima({ id: 'vitima-b', veiculoId: 'veiculo-a', nome: 'Vítima B · passageira traseira', idAncora: 'traseiroEsquerdo', idPerfil: 'adultoFeminino', estadoClinico: 'estavel', perceptivel: ['Consciente', 'Responde', 'Sem hemorragia visível'], urgencia: 'estavel', encarceramento: 'fisicoI', leituraEncarceramento: 'Lesões e posição devem ser avaliadas; não há peça prendendo diretamente a vítima.' }),
    ],
    pausas: ['A vítima A tem maior prioridade clínica; a vítima B interfere na rota. Qual sequência você justifica?'],
  },
  {
    id: 'dois-veiculos',
    nome: '4 · Dois veículos',
    resumo: 'Hemorragia visível e acesso fácil no A; inconsciência e acesso complexo no B.',
    objetivo: 'Triagem inicial sem reduzir a decisão a um ranking automático.',
    veiculos: [
      { id: 'veiculo-a', nome: 'Veículo A', configuracao: 'sobreRodas', estabilidadeInicial: true, riscosIniciais: [], acessoInicial: 'livre' },
      { id: 'veiculo-b', nome: 'Veículo B', configuracao: 'lateral', decubito: 'dorsal', sentidoCabeca: 'frente', estabilidadeInicial: false, riscosIniciais: ['veículo lateralizado e instável'], acessoInicial: 'limitado' },
    ],
    vitimas: [
      vitima({ id: 'vitima-a', veiculoId: 'veiculo-a', nome: 'Vítima A · motorista', idAncora: 'motorista', estadoClinico: 'grave', perceptivel: ['Consciente', 'Responde', 'Hemorragia externa importante', 'Acesso fácil'], urgencia: 'maior', encarceramento: 'mecanico', leituraEncarceramento: 'A porta está deformada, sem prender diretamente a vítima.', xabcde: { X: { resumo: 'Hemorragia importante em membro inferior', detalhe: 'Sangramento externo de grande volume é perceptível na avaliação rápida.', regiao: 'membroInferior' } } }),
      vitima({ id: 'vitima-b', veiculoId: 'veiculo-b', nome: 'Vítima B · motorista', idAncora: 'motorista', estadoClinico: 'grave', perceptivel: ['Não responde', 'Respiração presente', 'Sem hemorragia externa evidente', 'Acesso complexo'], urgencia: 'avaliar', encarceramento: 'fisicoII', leituraEncarceramento: 'A deformação lateral limita o corpo diretamente.', xabcde: { D: { resumo: 'Inconsciente', detalhe: 'Não responde ao chamado nesta avaliação simplificada.', regiao: 'cabeca' }, E: { resumo: 'Veículo tombado e espaço reduzido', detalhe: 'Acesso e retirada dependem da estabilização e da criação de uma rota segura.', regiao: 'corpoTodo' } } }),
    ],
    pausas: ['Quem recebe avaliação/intervenção primeiro, considerando achado clínico e acesso?'],
  },
  {
    id: 'capotado-pa-forno',
    nome: '5 · Capotado — pá de forno',
    resumo: 'Veículo invertido e inclinado: cofre do motor apoiado no solo, traseira mais alta.',
    objetivo: 'Executar a pá de forno e retirar o conjunto prancha–vítima sem giro da vítima.',
    veiculos: [{ id: 'veiculo-a', nome: 'Veículo A', configuracao: 'invertido', decubito: 'dorsal', sentidoCabeca: 'frente', estabilidadeInicial: false, riscosIniciais: ['veículo invertido'], acessoInicial: 'limitado' }],
    vitimas: [vitima({ id: 'vitima-a', veiculoId: 'veiculo-a', nome: 'Vítima A · sobre o teto', idAncora: 'motorista', estadoClinico: 'estavel', perceptivel: ['Consciente', 'Deitada sobre o teto', 'Saída preferencial pela traseira/porta-malas; portas como alternativa'], urgencia: 'estavel', encarceramento: 'fisicoI', leituraEncarceramento: 'A posição e as possíveis lesões impedem saída autônoma.' })],
    pausas: ['Antes de elevar a vítima, a prancha e a rota estão preparadas?'],
  },
  {
    id: 'tombado-pa-forno',
    nome: '6 · Tombado — pá de forno',
    resumo: 'Vítima deitada sobre a lateral baixa; saída preferencial pela traseira/porta-malas.',
    objetivo: 'Priorizar a traseira/porta-malas, usar o lado alto apenas quando adequado e executar a pá de forno sem giro da vítima.',
    veiculos: [{ id: 'veiculo-a', nome: 'Veículo A', configuracao: 'lateral', decubito: 'dorsal', sentidoCabeca: 'traseira', estabilidadeInicial: false, riscosIniciais: ['veículo lateralizado'], acessoInicial: 'limitado' }],
    vitimas: [vitima({ id: 'vitima-a', veiculoId: 'veiculo-a', nome: 'Vítima A · lateral baixa', idAncora: 'motorista', estadoClinico: 'estavel', perceptivel: ['Consciente', 'Deitada sobre a lateral baixa', 'Lado baixo indisponível'], urgencia: 'estavel', encarceramento: 'fisicoI', leituraEncarceramento: 'A posição impede saída autônoma e condiciona a rota.' })],
    pausas: ['A traseira/porta-malas é viável? Se não for, qual alternativa exige menor risco e por quê?'],
  },
]

/**
 * `extras` são cenários fora do catálogo fixo — hoje, cenários autorais do
 * professor (Fase 5B). Entram primeiro na busca pra um id autoral não poder
 * ser sombreado por um id de catálogo igual por acidente.
 */
export const cenarioExtracaoPorId = (
  id: string,
  extras: readonly CenarioExtracao[] = [],
): CenarioExtracao =>
  extras.find((cenario) => cenario.id === id) ??
  CENARIOS_EXTRACAO.find((cenario) => cenario.id === id) ??
  CENARIOS_EXTRACAO[0]!

/** Adapta o catálogo pedagógico à coleção já usada pelo 3D e pela geometria. */
export function ocupantesDoCenario(cenario: CenarioExtracao, veiculoId?: string): Ocupante[] {
  return cenario.vitimas
    .filter((item) => veiculoId == null || item.veiculoId === veiculoId)
    .map((item) => ({
      id: item.id,
      tipo: 'pessoa',
      nome: item.nome,
      idAncora: item.idAncora,
      idPerfil: item.idPerfil,
      retencao: 'comCinto',
      estadoClinico: item.estadoClinico,
    }))
}
